import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { Button } from '../ui/Button'

// ── Types ────────────────────────────────────────────────────────────

interface StagedSite {
  id: string
  site_id: string
  site_name: string
  host_name: string | null
  portal_site_id: string | null
  portal_site_name: string | null
  match_score: number | null
  match_status: string
  created_at: string | null
}

interface PortalSite {
  id: string
  name: string
  slug: string
  address?: string
  client_name?: string
  client_id?: string
}

interface Client {
  id: string
  company_name: string
  slug: string
}

type StagingTab = 'pending' | 'confirmed' | 'ignored'

// ── Component ────────────────────────────────────────────────────────

export default function UniFiStagingQueue({ connectionId }: { connectionId: string }) {
  const [tab, setTab] = useState<StagingTab>('pending')
  const [pending, setPending] = useState<StagedSite[]>([])
  const [confirmed, setConfirmed] = useState<StagedSite[]>([])
  const [ignored, setIgnored] = useState<StagedSite[]>([])
  const [portalSites, setPortalSites] = useState<PortalSite[]>([])
  const [initialLoad, setInitialLoad] = useState(true)
  const [discovering, setDiscovering] = useState(false)
  const [discoverResult, setDiscoverResult] = useState<string | null>(null)
  const [bulkActioning, setBulkActioning] = useState(false)
  const [actioningIds, setActioningIds] = useState<Set<string>>(new Set())
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set())
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [searchOpen, setSearchOpen] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<PortalSite[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Create-site inline form state
  const [createMode, setCreateMode] = useState<string | null>(null) // staged site id
  const [createName, setCreateName] = useState('')
  const [createAddress, setCreateAddress] = useState('')
  const [createClientId, setCreateClientId] = useState('')
  const [createSaving, setCreateSaving] = useState(false)
  const [clients, setClients] = useState<Client[]>([])
  const [clientsLoaded, setClientsLoaded] = useState(false)
  // Bulk create & link state
  const [bulkCreateOpen, setBulkCreateOpen] = useState(false)
  const [bulkCreateClientId, setBulkCreateClientId] = useState('')
  const [bulkCreating, setBulkCreating] = useState(false)
  const [bulkCreateProgress, setBulkCreateProgress] = useState<string | null>(null)
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const bulkCreateRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Click-outside handler to close dropdown (portal-aware)
  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as Node
    const inDropdown = dropdownRef.current && dropdownRef.current.contains(target)
    const inTrigger = searchOpen && triggerRefs.current.get(searchOpen)?.contains(target)
    if (!inDropdown && !inTrigger) {
      setSearchOpen(null)
      setSearchQuery('')
      setSearchResults([])
      setCreateMode(null)
      setDropdownPos(null)
    }
  }, [searchOpen])

  useEffect(() => {
    if (searchOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [searchOpen, handleClickOutside])

  async function refreshData() {
    try {
      const [p, c, i, sites] = await Promise.all([
        api.get<StagedSite[]>('/unifi/staged?status=pending'),
        api.get<StagedSite[]>('/unifi/staged?status=confirmed'),
        api.get<StagedSite[]>('/unifi/staged?status=ignored'),
        api.get<{ data: PortalSite[] }>('/sites?limit=200'),
      ])
      setPending(p)
      setConfirmed(c)
      setIgnored(i)
      setPortalSites(sites.data || [])
      setError(null)
    } catch {
      setError('Failed to load site data.')
    } finally {
      setInitialLoad(false)
    }
  }

  useEffect(() => { refreshData() }, [])

  // Clear selection when switching tabs
  useEffect(() => { setSelected(new Set()) }, [tab])

  // Debounced search for portal sites via API
  useEffect(() => {
    if (!searchOpen) return

    // Cancel previous timer
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)

    const query = searchQuery.trim()

    // Always search via API to get fresh results with client_name
    setSearchLoading(true)
    searchTimerRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ limit: '20' })
        if (query) params.set('search', query)
        const result = await api.get<{ data: PortalSite[] }>(`/sites?${params}`)
        setSearchResults(result.data || [])
      } catch (err) {
        console.error('[UniFiStagingQueue] site search failed:', err)
        // Fall back to client-side filter of preloaded sites
        const filtered = query
          ? portalSites.filter(ps =>
              ps.name.toLowerCase().includes(query.toLowerCase()) ||
              (ps.address && ps.address.toLowerCase().includes(query.toLowerCase())) ||
              (ps.client_name && ps.client_name.toLowerCase().includes(query.toLowerCase()))
            )
          : portalSites
        setSearchResults(filtered.slice(0, 20))
      } finally {
        setSearchLoading(false)
      }
    }, query ? 250 : 0) // No delay for initial load, 250ms debounce for typing

    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }
  }, [searchQuery, searchOpen, portalSites])

  // Load clients when create mode or bulk create is opened
  useEffect(() => {
    if ((createMode || bulkCreateOpen) && !clientsLoaded) {
      api.get<{ data: Client[] }>('/clients?limit=100')
        .then(r => { setClients(r.data || []); setClientsLoaded(true) })
        .catch(() => { setClients([]); setClientsLoaded(true) })
    }
  }, [createMode, bulkCreateOpen, clientsLoaded])

  // Click-outside handler for bulk create dropdown
  useEffect(() => {
    if (!bulkCreateOpen) return
    function handleBulkClickOutside(e: MouseEvent) {
      if (bulkCreateRef.current && !bulkCreateRef.current.contains(e.target as Node)) {
        setBulkCreateOpen(false)
        setBulkCreateClientId('')
      }
    }
    document.addEventListener('mousedown', handleBulkClickOutside)
    return () => document.removeEventListener('mousedown', handleBulkClickOutside)
  }, [bulkCreateOpen])

  async function handleDiscover() {
    setDiscovering(true)
    setError(null)
    setDiscoverResult(null)
    try {
      const result = await api.post<{ discovered: number; new: number }>('/unifi/discover', { connection_id: connectionId })
      await refreshData()
      if (result.new === 0) {
        setDiscoverResult('All sites already discovered. No new sites found.')
      }
    } catch {
      setError('Discovery failed. Check your UniFi connection and try again.')
    } finally {
      setDiscovering(false)
    }
  }

  async function handleBulkAction(action: 'confirm' | 'ignore') {
    if (selected.size === 0) return
    setBulkActioning(true)
    setError(null)
    try {
      const currentList = tab === 'pending' ? pending : tab === 'confirmed' ? confirmed : ignored
      const actions = currentList
        .filter(s => selected.has(s.id))
        .map(s => ({ siteId: s.id, action }))
      await api.post('/unifi/staged/bulk', { actions })
      setSelected(new Set())
      await refreshData()
    } catch {
      setError(`Bulk ${action} failed.`)
    } finally {
      setBulkActioning(false)
    }
  }

  async function handleAction(site: StagedSite, action: 'confirm' | 'ignore' | 'unlink' | 'restore') {
    setActioningIds(prev => new Set(prev).add(site.id))
    setError(null)
    try {
      const actualAction = action === 'restore' ? 'unlink' : action
      await api.patch(`/unifi/staged/${site.id}`, { action: actualAction })
      setRemovingIds(prev => new Set(prev).add(site.id))
      setTimeout(async () => {
        setRemovingIds(prev => { const n = new Set(prev); n.delete(site.id); return n })
        setSelected(prev => { const n = new Set(prev); n.delete(site.id); return n })
        await refreshData()
      }, 300)
    } catch {
      setError(`Failed to ${action} site "${site.site_name}".`)
    } finally {
      setActioningIds(prev => { const n = new Set(prev); n.delete(site.id); return n })
    }
  }

  async function handleReassign(site: StagedSite, portalSiteId: string) {
    setActioningIds(prev => new Set(prev).add(site.id))
    setSearchOpen(null)
    setSearchQuery('')
    setSearchResults([])
    setCreateMode(null)
    setDropdownPos(null)
    setError(null)
    try {
      await api.patch(`/unifi/staged/${site.id}`, { action: 'reassign', portalSiteId })
      setRemovingIds(prev => new Set(prev).add(site.id))
      setTimeout(async () => {
        setRemovingIds(prev => { const n = new Set(prev); n.delete(site.id); return n })
        await refreshData()
      }, 300)
    } catch {
      setError(`Failed to reassign site "${site.site_name}".`)
    } finally {
      setActioningIds(prev => { const n = new Set(prev); n.delete(site.id); return n })
    }
  }

  async function handleCreateSite(stagedSite: StagedSite) {
    if (!createClientId || !createName.trim()) return
    setCreateSaving(true)
    setError(null)
    try {
      const newSite = await api.post<{ id: string }>(`/clients/${createClientId}/sites`, {
        client_id: createClientId,
        name: createName.trim(),
        address: createAddress.trim() || undefined,
      })
      // Immediately link the new site to the staged UniFi site
      await api.patch(`/unifi/staged/${stagedSite.id}`, { action: 'reassign', portalSiteId: newSite.id })
      setCreateMode(null)
      setSearchOpen(null)
      setDropdownPos(null)
      setCreateName('')
      setCreateAddress('')
      setCreateClientId('')
      setRemovingIds(prev => new Set(prev).add(stagedSite.id))
      setTimeout(async () => {
        setRemovingIds(prev => { const n = new Set(prev); n.delete(stagedSite.id); return n })
        await refreshData()
      }, 300)
    } catch {
      setError(`Failed to create site "${createName}".`)
    } finally {
      setCreateSaving(false)
    }
  }

  function openCreateMode(site: StagedSite) {
    setCreateMode(site.id)
    setCreateName(site.site_name)
    setCreateAddress('')
    setCreateClientId('')
  }

  async function handleBulkCreateAndLink() {
    if (!bulkCreateClientId) return
    const selectedPending = pending.filter(s => selected.has(s.id))
    if (selectedPending.length === 0) return

    setBulkCreating(true)
    setError(null)
    let created = 0
    const total = selectedPending.length

    try {
      for (const site of selectedPending) {
        setBulkCreateProgress(`Creating ${++created} of ${total}...`)
        const newSite = await api.post<{ id: string }>(`/clients/${bulkCreateClientId}/sites`, {
          client_id: bulkCreateClientId,
          name: site.site_name,
        })
        await api.patch(`/unifi/staged/${site.id}`, { action: 'reassign', portalSiteId: newSite.id })
        await api.patch(`/unifi/staged/${site.id}`, { action: 'confirm' })
      }
      setBulkCreateOpen(false)
      setBulkCreateClientId('')
      setSelected(new Set())
      await refreshData()
    } catch {
      setError(`Bulk create failed after ${created} of ${total} sites. Refresh to see progress.`)
    } finally {
      setBulkCreating(false)
      setBulkCreateProgress(null)
    }
  }

  function matchBadge(score: number | null) {
    if (score !== null && score !== undefined && score >= 75) {
      return <span style={badgeStyle('var(--ok)', 'var(--ok-m)')}>High Match</span>
    }
    if (score !== null && score !== undefined && score >= 50) {
      return <span style={badgeStyle('var(--warn)', 'var(--warn-m)')}>Partial Match</span>
    }
    return <span style={badgeStyle('var(--text-disabled)', 'var(--bg-overlay)')}>No Match</span>
  }

  function toggleSelect(id: string) {
    setSelected(prev => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  function toggleSelectAll(list: StagedSite[]) {
    const allSelected = list.every(s => selected.has(s.id))
    if (allSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(list.map(s => s.id)))
    }
  }

  function openDropdown(siteId: string) {
    const isOpen = searchOpen === siteId
    if (isOpen) {
      setSearchOpen(null)
      setDropdownPos(null)
    } else {
      const btn = triggerRefs.current.get(siteId)
      if (btn) {
        const rect = btn.getBoundingClientRect()
        setDropdownPos({
          top: rect.bottom + 4,
          left: Math.max(8, rect.right - 300),
          width: 300,
        })
      }
      setSearchOpen(siteId)
    }
    setSearchQuery('')
    setSearchResults([])
    setSearchLoading(true)
    setCreateMode(null)
  }

  if (initialLoad) {
    return (
      <div style={{ ...cardStyle, marginTop: 16 }}>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading site mappings...</p>
      </div>
    )
  }

  return (
    <div style={{ ...cardStyle, marginTop: 16, padding: 0 }}>
      <style>{stagingCSS}</style>

      {/* Section header with summary */}
      <div style={{ padding: '16px 20px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ ...cardTitleStyle, marginBottom: 0 }}>Site Mapping</h3>
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            {pending.length} pending &middot; {confirmed.length} confirmed &middot; {ignored.length} ignored
          </span>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div style={{ margin: '0 20px 8px', padding: '8px 12px', background: 'var(--err-m)', border: '1px solid var(--err-m)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 12, color: 'var(--danger)', fontWeight: 500 }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 14, padding: '0 4px', lineHeight: 1 }}>&times;</button>
        </div>
      )}

      {/* Discover result banner */}
      {discoverResult && (
        <div style={{ margin: '0 20px 8px', padding: '8px 12px', background: 'var(--info-m)', border: '1px solid var(--info-m)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{discoverResult}</span>
          <button onClick={() => setDiscoverResult(null)} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 14, padding: '0 4px', lineHeight: 1 }}>&times;</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border-subtle)', padding: '0 20px' }}>
        {([
          { key: 'pending' as StagingTab, label: 'Pending', count: pending.length },
          { key: 'confirmed' as StagingTab, label: 'Confirmed', count: confirmed.length },
          { key: 'ignored' as StagingTab, label: 'Ignored', count: ignored.length },
        ]).map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`staging-tab ${tab === t.key ? 'active' : ''}`}
          >
            {t.label}
            {t.count > 0 && (
              <span className={`staging-tab-badge ${t.key === 'pending' && tab === 'pending' ? 'accent' : ''}`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: 0 }}>

        {/* ── Pending Tab ────────────────────────────────────────── */}
        {tab === 'pending' && (
          <div>
            {/* Header row with actions */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: pending.length > 0 ? '1px solid var(--border-subtle)' : 'none' }}>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {pending.length > 0 ? `${pending.length} site${pending.length !== 1 ? 's' : ''} pending review` : ''}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                {selected.size > 0 && (
                  <>
                    <div ref={bulkCreateRef} style={{ position: 'relative', display: 'inline-block' }}>
                      <Button
                        size="sm"
                        onClick={() => setBulkCreateOpen(!bulkCreateOpen)}
                        disabled={bulkCreating}
                        loading={bulkCreating}
                        style={{ background: 'var(--accent)' }}
                      >
                        {bulkCreating ? (bulkCreateProgress || 'Creating...') : `Create & Link All (${selected.size})`}
                      </Button>
                      {bulkCreateOpen && (
                        <div className="staging-dropdown" style={{ position: 'absolute', top: '100%', marginTop: 4, width: 280, right: 0, left: 'auto', zIndex: 50 }}>
                          <div style={{ padding: 2 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                              Create & Link {selected.size} Site{selected.size !== 1 ? 's' : ''}
                            </div>
                            <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8, lineHeight: 1.4 }}>
                              Creates a portal site for each selected row using its UniFi name, assigns to the chosen client, and confirms.
                            </p>
                            {!clientsLoaded ? (
                              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', padding: '8px 0', textAlign: 'center' }}>Loading clients...</div>
                            ) : clients.length === 0 ? (
                              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', padding: '8px 0' }}>
                                No clients yet. <Link to="/clients" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Create a client</Link> first.
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <select
                                  value={bulkCreateClientId}
                                  onChange={e => setBulkCreateClientId(e.target.value)}
                                  className="input"
                                  style={{ fontSize: 11, padding: '6px 8px' }}
                                >
                                  <option value="">Select client...</option>
                                  {clients.map(cl => (
                                    <option key={cl.id} value={cl.id}>{cl.company_name}</option>
                                  ))}
                                </select>
                                <Button
                                  size="sm"
                                  onClick={handleBulkCreateAndLink}
                                  disabled={!bulkCreateClientId || bulkCreating}
                                  loading={bulkCreating}
                                  style={{ width: '100%', opacity: !bulkCreateClientId ? 0.5 : 1 }}
                                >
                                  {bulkCreating ? (bulkCreateProgress || 'Creating...') : `Create ${selected.size} Site${selected.size !== 1 ? 's' : ''}`}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleBulkAction('confirm')}
                      disabled={bulkActioning}
                      loading={bulkActioning}
                    >
                      {bulkActioning ? 'Processing...' : `Confirm Selected (${selected.size})`}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleBulkAction('ignore')}
                      disabled={bulkActioning}
                    >
                      Ignore Selected ({selected.size})
                    </Button>
                  </>
                )}
                <Button
                  size="sm"
                  onClick={handleDiscover}
                  disabled={discovering}
                  loading={discovering}
                >
                  {discovering ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span className="staging-spinner" /> Discovering...
                    </span>
                  ) : 'Discover Sites'}
                </Button>
              </div>
            </div>

            {pending.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 12 }}>
                  All sites reviewed. Run Discovery to check for new sites.
                </p>
                <Button variant="secondary" size="sm" onClick={handleDiscover} disabled={discovering} loading={discovering}>
                  {discovering ? 'Discovering...' : 'Discover Sites'}
                </Button>
              </div>
            ) : (
              <div>
                {/* Select-all row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 20px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-overlay)' }}>
                  <input
                    type="checkbox"
                    checked={pending.length > 0 && pending.every(s => selected.has(s.id))}
                    onChange={() => toggleSelectAll(pending)}
                    style={{ accentColor: 'var(--accent)' }}
                  />
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Select all</span>
                </div>

                {pending.map(site => (
                  <div
                    key={site.id}
                    className={`staging-row ${removingIds.has(site.id) ? 'removing' : ''}`}
                  >
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={selected.has(site.id)}
                      onChange={() => toggleSelect(site.id)}
                      style={{ accentColor: 'var(--accent)', flexShrink: 0 }}
                    />

                    {/* Site info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{site.site_name}</span>
                        {matchBadge(site.match_score)}
                      </div>
                      {site.host_name && (
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{site.host_name}</div>
                      )}
                    </div>

                    {/* Matched portal site / trigger button */}
                    <div style={{ flexShrink: 0, minWidth: 200 }}>
                      {site.portal_site_id && site.portal_site_name ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{
                            display: 'inline-block', padding: '3px 8px', fontSize: 11, fontWeight: 600,
                            background: 'var(--bg-overlay)', borderRadius: 4, color: 'var(--text-secondary)',
                          }}>
                            {site.portal_site_name}
                          </span>
                          <button
                            ref={el => { if (el) triggerRefs.current.set(site.id, el); }}
                            onClick={() => openDropdown(site.id)}
                            style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--accent)', cursor: 'pointer', fontWeight: 500 }}
                          >
                            change
                          </button>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          ref={el => { if (el) triggerRefs.current.set(site.id, el as HTMLButtonElement); }}
                          onClick={() => openDropdown(site.id)}
                          style={{ color: 'var(--accent)' }}
                        >
                          Select site...
                        </Button>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <Button
                        size="sm"
                        onClick={() => handleAction(site, 'confirm')}
                        disabled={!site.portal_site_id || actioningIds.has(site.id)}
                        style={{ opacity: site.portal_site_id ? 1 : 0.4 }}
                        title={!site.portal_site_id ? 'Select a portal site first' : ''}
                      >
                        {actioningIds.has(site.id) ? '...' : 'Confirm'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAction(site, 'ignore')}
                        disabled={actioningIds.has(site.id)}
                        style={{ color: 'var(--text-tertiary)' }}
                      >
                        Ignore
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Portal dropdown — rendered at document.body to escape overflow:hidden */}
            {searchOpen && dropdownPos && (() => {
              const site = pending.find(s => s.id === searchOpen)
              if (!site) return null
              return createPortal(
                <div
                  ref={dropdownRef}
                  className="staging-dropdown"
                  style={{
                    position: 'fixed',
                    top: dropdownPos.top,
                    left: dropdownPos.left,
                    width: createMode === site.id ? 320 : dropdownPos.width,
                    zIndex: 9999,
                  }}
                >
                  {createMode === site.id ? (
                    /* ── Create New Site Form ── */
                    <div style={{ padding: 2 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Create New Site</span>
                        <button
                          onClick={() => setCreateMode(null)}
                          style={{ background: 'none', border: 'none', fontSize: 13, color: 'var(--text-tertiary)', cursor: 'pointer', padding: '0 2px' }}
                        >&times;</button>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <input
                          value={createName}
                          onChange={e => setCreateName(e.target.value)}
                          placeholder="Site name"
                          className="input"
                          style={{ fontSize: 11, padding: '6px 8px' }}
                        />
                        <select
                          value={createClientId}
                          onChange={e => setCreateClientId(e.target.value)}
                          className="input"
                          style={{ fontSize: 11, padding: '6px 8px' }}
                        >
                          <option value="">Select client...</option>
                          {clients.map(cl => (
                            <option key={cl.id} value={cl.id}>{cl.company_name}</option>
                          ))}
                        </select>
                        {clientsLoaded && clients.length === 0 && (
                          <div style={{ fontSize: 10, color: 'var(--text-tertiary)', padding: '2px 0' }}>
                            No clients found. <Link to="/clients" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Create a client</Link> first.
                          </div>
                        )}
                        <input
                          value={createAddress}
                          onChange={e => setCreateAddress(e.target.value)}
                          placeholder="Address (optional)"
                          className="input"
                          style={{ fontSize: 11, padding: '6px 8px' }}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleCreateSite(site)}
                          disabled={createSaving || !createClientId || !createName.trim()}
                          loading={createSaving}
                          style={{ width: '100%', opacity: (!createClientId || !createName.trim()) ? 0.5 : 1 }}
                        >
                          {createSaving ? 'Creating...' : 'Create & Link Site'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* ── Search Results ── */
                    <>
                      <input
                        autoFocus
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Search all portal sites..."
                        className="input"
                        style={{ fontSize: 11, padding: '6px 8px', marginBottom: 4 }}
                        onKeyDown={e => { if (e.key === 'Escape') { setSearchOpen(null); setSearchQuery(''); setSearchResults([]); setDropdownPos(null) } }}
                      />
                      <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                        {searchLoading ? (
                          <div style={{ padding: '8px', fontSize: 11, color: 'var(--text-tertiary)', textAlign: 'center' }}>Searching...</div>
                        ) : searchResults.length > 0 ? (
                          searchResults.map(ps => (
                            <button
                              key={ps.id}
                              onClick={() => handleReassign(site, ps.id)}
                              className="staging-dropdown-item"
                            >
                              <span style={{ fontSize: 12, color: 'var(--text)' }}>{ps.name}</span>
                              {(ps.address || ps.client_name) && (
                                <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
                                  {[ps.client_name, ps.address].filter(Boolean).join(' — ')}
                                </span>
                              )}
                            </button>
                          ))
                        ) : (
                          <div style={{ padding: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>
                              {searchQuery ? 'No matching sites' : 'No portal sites yet'}
                            </div>
                            <Button
                              size="sm"
                              onClick={() => openCreateMode(site)}
                              style={{ width: '100%' }}
                            >
                              + Create New Site
                            </Button>
                          </div>
                        )}
                      </div>
                      {/* Create New Site button */}
                      <div style={{ borderTop: '1px solid var(--border-subtle)', marginTop: 4, paddingTop: 4 }}>
                        <button
                          onClick={() => openCreateMode(site)}
                          className="staging-dropdown-item"
                          style={{ color: 'var(--accent)', fontWeight: 600, flexDirection: 'row', gap: 4, fontSize: 11 }}
                        >
                          + Create New Site
                        </button>
                      </div>
                    </>
                  )}
                </div>,
                document.body
              )
            })()}

          </div>
        )}

        {/* ── Confirmed Tab ──────────────────────────────────────── */}
        {tab === 'confirmed' && (
          <div>
            {confirmed.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
                  No confirmed sites yet. Review pending sites to get started.
                </p>
              </div>
            ) : (
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>UniFi Site</th>
                    <th>Portal Site</th>
                    <th>Match</th>
                    <th>Confirmed</th>
                    <th style={{ width: 80 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {confirmed.map(site => {
                    const linkedPortalSite = portalSites.find(ps => ps.id === site.portal_site_id)
                    return (
                      <tr key={site.id} className={removingIds.has(site.id) ? 'staging-row-removing' : ''}>
                        <td style={{ fontWeight: 500, color: 'var(--text)' }}>{site.site_name}</td>
                        <td>
                          {linkedPortalSite ? (
                            <Link
                              to={`/sites/${linkedPortalSite.slug}`}
                              style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 500, textDecoration: 'none' }}
                            >
                              {site.portal_site_name}
                            </Link>
                          ) : (
                            <span style={{ color: 'var(--text-tertiary)' }}>{site.portal_site_name || 'Unmatched'}</span>
                          )}
                        </td>
                        <td>{matchBadge(site.match_score)}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                          {site.created_at ? new Date(site.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAction(site, 'unlink')}
                            disabled={actioningIds.has(site.id)}
                            style={{ color: 'var(--danger)' }}
                          >
                            Unlink
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── Ignored Tab ────────────────────────────────────────── */}
        {tab === 'ignored' && (
          <div>
            {ignored.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>No ignored sites.</p>
              </div>
            ) : (
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Site Name</th>
                    <th>Host</th>
                    <th style={{ width: 80 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {ignored.map(site => (
                    <tr key={site.id} className={removingIds.has(site.id) ? 'staging-row-removing' : ''}>
                      <td style={{ fontWeight: 500, color: 'var(--text)' }}>{site.site_name}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{site.host_name || '—'}</td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAction(site, 'restore')}
                          disabled={actioningIds.has(site.id)}
                        >
                          Restore
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Styles ────────────────────────────────────────────────────────────

function badgeStyle(color: string, bg: string): React.CSSProperties {
  return {
    display: 'inline-block', padding: '2px 7px', fontSize: 10, fontWeight: 700,
    borderRadius: 10, color, background: bg, letterSpacing: '0.02em',
  }
}

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--shadow-xs)',
  padding: 20,
}

const cardTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: 16,
}

const stagingCSS = `
  .staging-tab {
    padding: 10px 14px;
    border: none;
    background: transparent;
    color: var(--text-tertiary);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.15s;
    font-family: var(--font-sans);
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .staging-tab:hover { color: var(--text-secondary); }
  .staging-tab.active {
    color: var(--accent);
    border-bottom-color: var(--accent);
  }
  .staging-tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    font-size: 10px;
    font-weight: 700;
    background: var(--bg-overlay);
    color: var(--text-tertiary);
  }
  .staging-tab-badge.accent {
    background: var(--accent-muted);
    color: var(--accent);
  }
  .staging-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 20px;
    border-bottom: 1px solid var(--border-subtle);
    transition: all 0.25s ease;
    max-height: 80px;
    overflow: hidden;
    opacity: 1;
  }
  .staging-row:hover {
    background: var(--bg-overlay);
  }
  .staging-row.removing {
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
    opacity: 0;
    border-bottom-width: 0;
  }
  .staging-row-removing td {
    opacity: 0;
    transition: opacity 0.25s;
  }
  .staging-dropdown {
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 6px;
    box-shadow: var(--shadow-lg);
  }
  .staging-dropdown-item {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 6px 8px;
    border: none;
    background: transparent;
    border-radius: 4px;
    cursor: pointer;
    text-align: left;
    font-family: var(--font-sans);
  }
  .staging-dropdown-item:hover {
    background: var(--bg-overlay);
  }
  .staging-spinner {
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: staging-spin 0.6s linear infinite;
  }
  @keyframes staging-spin {
    to { transform: rotate(360deg); }
  }
`
